<script setup lang="ts">
import UseSvg from "common/vue/components/UseSvg.vue";
import { getFormPayload } from "components/jiffy/vue-product-details-dtf/utils";
import { computed, ref } from "vue";
import { validatePhoneNumber } from "common/utils/validators/phone";
import { useDtfStore } from "../../store";

const store = useDtfStore();

const showDesignServicesDeliveryBuffer = computed(() => store.showDesignServicesDeliveryBuffer);
const isSmsTransactionalSubscribed = computed(() => store.messagingConsents?.smsTransactional);
const isSmsTransactionalSubscriptionPdpEnabled = computed(
  () => store.smsTransactionalSubscriptionPdpEnabled
);

const subscribePhone = computed({
  get: () => store.smsTransactionalPhone,
  set: value => {
    store.setSmsTransactionalPhone(value);
  }
});
const subscribePhoneError = ref("");
let subscribePhoneValidationTimeout: number | undefined;

function formatPhoneNumber(input: string): string {
  let cleanedInput = input.replace(/\D/g, "");

  // Strip leading '1' if we have 11 digits (US/Canada country code)
  if (cleanedInput.length === 11 && cleanedInput[0] === "1") {
    cleanedInput = cleanedInput.slice(1);
  }

  let formatted = "";

  if (cleanedInput.length <= 3) {
    formatted = cleanedInput;
  } else if (cleanedInput.length <= 6) {
    formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3)}`;
  } else {
    formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3, 6)}-${cleanedInput.slice(6, 10)}`;
  }

  return formatted;
}

function calculateNewCursorPosition(formatted: string, adjustedOldDigits: number): number {
  // If cursor was at the very beginning (0 digits before it), keep it there
  if (adjustedOldDigits === 0) {
    return 0;
  }

  let newCursorPosition = 0;
  let digitCount = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (formatted[i]?.match(/\d/)) {
      digitCount += 1;
      if (digitCount === adjustedOldDigits) {
        newCursorPosition = i + 1;
        break;
      }
    }
  }

  // If we didn't find enough digits, place at end
  if (digitCount < adjustedOldDigits) {
    newCursorPosition = formatted.length;
  }

  return newCursorPosition;
}

function restorePreviousValue(target: HTMLInputElement, previousValue: string, prevCursor: number) {
  target.value = previousValue;
  target.setSelectionRange(prevCursor, prevCursor);
  subscribePhone.value = previousValue;
}

function salvageDigits(target: HTMLInputElement, allDigits: string) {
  const salvaged = allDigits[0] === "1" ? allDigits.slice(0, 11) : allDigits.slice(0, 10);
  const formatted = formatPhoneNumber(salvaged);
  target.value = formatted;
  target.setSelectionRange(formatted.length, formatted.length);
  // eslint-disable-next-line dot-notation
  target.dataset["formattedValue"] = formatted;
  // eslint-disable-next-line dot-notation
  target.dataset["cursorPosition"] = formatted.length.toString();
  subscribePhone.value = formatted;
}

function handleInvalidLength(target: HTMLInputElement, previousValue: string, allDigits: string) {
  if (previousValue) {
    const prevCursor = parseInt(
      // eslint-disable-next-line dot-notation
      target.dataset["cursorPosition"] || previousValue.length.toString(),
      10
    );
    restorePreviousValue(target, previousValue, prevCursor);
  } else if (allDigits.length >= 11) {
    salvageDigits(target, allDigits);
  }
}

function handleEmptyValue(target: HTMLInputElement) {
  subscribePhone.value = "";
  // eslint-disable-next-line dot-notation
  target.dataset["formattedValue"] = "";
  // eslint-disable-next-line dot-notation
  target.dataset["cursorPosition"] = "0";
  if (subscribeButtonText.value !== "Subscribe") {
    subscribeButtonText.value = "Subscribe";
  }
}

function applyFormattedValue(
  target: HTMLInputElement,
  formatted: string,
  options: { oldDigitsBeforeCursor: number; strippedLeading1: boolean }
) {
  const adjustedOldDigits =
    options.strippedLeading1 && options.oldDigitsBeforeCursor > 0
      ? options.oldDigitsBeforeCursor - 1
      : options.oldDigitsBeforeCursor;

  const newCursorPosition = calculateNewCursorPosition(formatted, adjustedOldDigits);

  target.value = formatted;
  target.setSelectionRange(newCursorPosition, newCursorPosition);
  // eslint-disable-next-line dot-notation
  target.dataset["formattedValue"] = formatted;
  // eslint-disable-next-line dot-notation
  target.dataset["cursorPosition"] = newCursorPosition.toString();
  subscribePhone.value = formatted;

  if (subscribeButtonText.value !== "Subscribe") {
    subscribeButtonText.value = "Subscribe";
  }
}
const subscribeButtonText = computed({
  get: () => store.smsTransactionalSubscribeButtonText,
  set: value => {
    store.setSmsTransactionalSubscribeButtonText(value);
  }
});
const isSubscribePhoneValid = computed(
  () => validatePhoneNumber(subscribePhone.value) && !subscribePhoneError.value
);

function validateSubscribePhone(phone: string) {
  const trimmedPhone = phone.trim();

  if (!trimmedPhone) {
    subscribePhoneError.value = "";
    return false;
  }

  if (!validatePhoneNumber(trimmedPhone)) {
    subscribePhoneError.value = "Please enter a valid 10-digit phone number";
    return false;
  }

  subscribePhoneError.value = "";

  return true;
}

function getConsentParams() {
  return {
    spree_user: {
      preferences: {
        consent: {
          sms_transactional: true
        }
      }
    }
  };
}

async function handleSubscribeClick() {
  const isValid = validateSubscribePhone(subscribePhone.value);
  if (!isValid) return;

  const formPayload = getFormPayload() as any;
  const orderNumber = formPayload.order_number;

  if (!orderNumber) {
    subscribePhoneError.value = "Unable to subscribe right now. Please refresh the page.";
    return;
  }

  const csrfMetaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  const csrfToken = csrfMetaTag ? csrfMetaTag.content : "";

  try {
    const response = await fetch(`/api/orders/${orderNumber}/update_redraw_phone`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
      },
      body: JSON.stringify({
        phone: subscribePhone.value,
        force_phone_update: true,
        consent_source: "transfer-pdp",
        ...getConsentParams()
      })
    });

    const data = await response.json();

    if (response.ok && data && data.success) {
      subscribePhoneError.value = "";
      subscribeButtonText.value = "Submitted";
    } else if (data && data.error) {
      subscribePhoneError.value = data.error;
    } else {
      subscribePhoneError.value = "Failed to save phone number";
    }
  } catch (error) {
    subscribePhoneError.value = "An error occurred. Please try again.";
  }
}

function handleSubscribeInput(event: Event) {
  subscribePhoneError.value = "";

  const target = event.target as HTMLInputElement;
  const cursorPosition = target.selectionStart || 0;
  const currentValue = target.value;
  const allDigits = currentValue.replace(/\D/g, "");
  const isValidLength = allDigits.length <= 10 || (allDigits.length === 11 && allDigits[0] === "1");
  // eslint-disable-next-line dot-notation
  const previousValue = target.dataset["formattedValue"] || "";

  if (!isValidLength) {
    handleInvalidLength(target, previousValue, allDigits);
    return;
  }

  if (!currentValue) {
    handleEmptyValue(target);
    return;
  }

  const oldDigitsBeforeCursor = currentValue.slice(0, cursorPosition).replace(/\D/g, "").length;
  const formatted = formatPhoneNumber(currentValue);
  const strippedLeading1 = allDigits.length === 11 && allDigits[0] === "1";

  applyFormattedValue(target, formatted, { oldDigitsBeforeCursor, strippedLeading1 });

  if (subscribePhoneValidationTimeout !== undefined) {
    clearTimeout(subscribePhoneValidationTimeout);
  }

  subscribePhoneValidationTimeout = window.setTimeout(() => {
    validateSubscribePhone(subscribePhone.value);
  }, 1000);
}
</script>

<template>
  <div
    v-if="!isSmsTransactionalSubscribed && isSmsTransactionalSubscriptionPdpEnabled"
    class="design-services__subscribe"
  >
    <div class="design-services__subscribe-label">Get notified when your edits are ready</div>
    <div class="design-services__subscribe-fields">
      <input
        :value="subscribePhone"
        type="tel"
        class="design-services__subscribe-input"
        placeholder="(xxx) xxx-xxxx"
        data-testid="design-services-subscribe-phone-input"
        :disabled="subscribeButtonText === 'Submitted'"
        @input="handleSubscribeInput($event)"
      />
      <button
        type="button"
        class="design-services__subscribe-button"
        data-testid="design-services-subscribe-button"
        :class="{
          'design-services__subscribe-button--submitted': subscribeButtonText === 'Submitted'
        }"
        :disabled="!isSubscribePhoneValid"
        @click="handleSubscribeClick"
      >
        <span class="design-services__subscribe-button-text">
          {{ subscribeButtonText }}
          <span
            v-if="subscribeButtonText !== 'Submitted'"
            class="design-services__subscribe-button-asterisk"
          >
            *
          </span>
        </span>
      </button>
    </div>
    <div
      v-if="subscribePhoneError"
      class="design-services__subscribe-error"
      data-testid="design-services-subscribe-error"
    >
      {{ subscribePhoneError }}
    </div>
    <div
      class="design-services__subscribe-note"
      :class="{
        'design-services__subscribe-note--submitted': subscribeButtonText === 'Submitted'
      }"
    >
      <template v-if="subscribeButtonText === 'Submitted'">
        <span class="design-services__subscribe-note-icon">
          <UseSvg icon="check-circle" class="design-services__subscribe-note-icon-svg" />
        </span>
        <span class="design-services__subscribe-note-text">
          Be on the lookout for a SMS notification from Jiffy with your redrawn image
        </span>
      </template>
      <template v-else>
        <template v-if="showDesignServicesDeliveryBuffer">
          *Edits add 1 day to delivery. Enter your mobile number to receive transactional SMS
          updates. Message and data rates may apply.
        </template>
        <template v-else>
          *Enter your mobile number to receive transactional SMS updates. Message and data rates may
          apply.
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";

.design-services__subscribe-label {
  font-size: 14px;
  margin-bottom: 4px;
}

.design-services__subscribe {
  margin-top: 16px;
}

.design-services__subscribe-fields {
  display: flex;
  align-items: center;
  gap: 8px;
}

.design-services__subscribe-input {
  flex: 1 1 auto;
  padding: 11px;
  border: 1px solid $app-gray-modern-400;
  border-radius: 8px;
  font-size: 14px;
}

.design-services__subscribe-button {
  flex: 0 0 auto;
  padding: 8px 16px;
  background-color: transparent;
  color: #414192;
  border: 2px solid #414192;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  &--submitted {
    background-color: #00a65f;
    color: $white;
    border: none;
  }
}

.design-services__subscribe-button-text {
  position: relative;
  display: inline-block;
}

.design-services__subscribe-button-asterisk {
  position: absolute;
  top: -3px;
  right: -8px;
  font-size: 20px;
}

.design-services__subscribe-note {
  margin-top: 8px;
  font-size: 12px;
  color: #444444;
  display: flex;
}

.design-services__subscribe-note--submitted {
  background-color: #dcfce7;
  border-radius: 8px;
  padding: 8px;
  align-items: center;
}

.design-services__subscribe-note-icon {
  display: flex;
  margin-right: 8px;
  vertical-align: middle;
  color: #00a65f;
  width: 20px;
  height: 20px;
}

.design-services__subscribe-note-icon-svg {
  width: 100%;
  height: 100%;
}

@media (max-width: 767px) {
  .design-services__subscribe-note-icon {
    width: 30px;
    height: 30px;
  }
}

.design-services__subscribe-note-text {
  vertical-align: middle;
  color: black;
}

.design-services__subscribe-error {
  margin-top: 4px;
  font-size: 12px;
  color: $red-error;
}
</style>
